//src/services/risks.ts
import { api } from "@/lib/apiClient";
import type { GroCrudScope } from "@/lib/groCrudScope";
import {
  createCompanyGroRisk,
  createCompanyGroRiskActionLink,
  deleteCompanyGroRiskActionLink,
  getCompanyGroTemplates,
  setCompanyGroRiskStatus,
  updateCompanyGroRisk,
} from "@/services/modelImports";
import type {
  RiskCatalog,
  RiskCatalogCreateIn,
  RiskCatalogDetail,
  RiskCatalogStatusIn,
  RiskCatalogUpdateIn,
  RiskActionPlanModelLink,
  RiskActionPlanModelLinkIn,
  RiskCategory,
  RiskTheme,
} from "@/types/risks";
import type { ActionPlanModel } from "@/types/actionPlanModels";

const isCompany = (scope: GroCrudScope) => scope === "company_model";

export async function listRiskCategories(
  params?: { risk_theme?: RiskTheme | ""; is_active?: boolean },
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) {
    const data = await getCompanyGroTemplates();
    return data.risk_categories.filter((item) => {
      if (params?.risk_theme && item.risk_theme !== params.risk_theme) return false;
      if (typeof params?.is_active === "boolean" && item.is_active !== params.is_active) return false;
      return true;
    }) as RiskCategory[];
  }

  return api.get<RiskCategory[]>("/risks/categories", {
    params: {
      risk_theme: params?.risk_theme || undefined,
      is_active: typeof params?.is_active === "boolean" ? params.is_active : undefined,
    },
  });
}

export async function listRisks(
  params?: {
    q?: string;
    risk_category_id?: string;
    risk_theme?: RiskTheme | "";
    is_active?: boolean;
  },
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) {
    const data = await getCompanyGroTemplates();
    const q = params?.q?.trim().toLocaleLowerCase("pt-BR") ?? "";
    return data.risks.filter((item) => {
      if (params?.risk_category_id && item.risk_category_id !== params.risk_category_id) return false;
      if (params?.risk_theme && item.risk_theme !== params.risk_theme) return false;
      if (typeof params?.is_active === "boolean" && item.is_active !== params.is_active) return false;
      if (!q) return true;
      return [item.code, item.name, item.technical_description, item.possible_damage]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase("pt-BR").includes(q));
    }) as RiskCatalog[];
  }

  return api.get<RiskCatalog[]>("/risks", {
    params: {
      q: params?.q || undefined,
      risk_category_id: params?.risk_category_id || undefined,
      risk_theme: params?.risk_theme || undefined,
      is_active: typeof params?.is_active === "boolean" ? params.is_active : undefined,
    },
  });
}

export async function getRisk(riskId: string, scope: GroCrudScope = "client") {
  if (isCompany(scope)) {
    const data = await getCompanyGroTemplates();
    const risk = data.risks.find((item) => item.id === riskId);
    if (!risk) throw new Error("Risco modelo não encontrado.");
    const linked = data.risk_action_plan_models
      .filter((item) => item.risk_catalog_id === riskId)
      .map((link) => {
        const plan = data.action_plan_models.find((item) => item.id === link.action_plan_model_id);
        return {
          id: link.id,
          action_plan_model_id: link.action_plan_model_id,
          risk_catalog_id: riskId,
          code: plan?.code ?? link.action_plan_code ?? "",
          title: plan?.title ?? link.action_plan_title,
          description: plan?.description ?? null,
          is_active: plan?.is_active ?? true,
          created_at: plan?.created_at ?? risk.created_at,
          updated_at: plan?.updated_at ?? risk.updated_at,
          items_count: plan?.items_count ?? plan?.items?.length ?? 0,
        } satisfies RiskActionPlanModelLink;
      });
    return { ...risk, linked_action_plan_models: linked } as RiskCatalogDetail;
  }
  return api.get<RiskCatalogDetail>(`/risks/${riskId}`);
}

export async function createRisk(payload: RiskCatalogCreateIn, scope: GroCrudScope = "client") {
  if (isCompany(scope)) return createCompanyGroRisk(payload);
  return api.post<RiskCatalog>("/risks", payload);
}

export async function updateRisk(
  riskId: string,
  payload: RiskCatalogUpdateIn,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) return updateCompanyGroRisk(riskId, payload);
  return api.put<RiskCatalog>(`/risks/${riskId}`, payload);
}

export async function setRiskStatus(
  riskId: string,
  payload: RiskCatalogStatusIn,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) return setCompanyGroRiskStatus(riskId, payload.is_active);
  return api.patch<RiskCatalog>(`/risks/${riskId}/status`, payload);
}

export async function deleteRisk(riskId: string, scope: GroCrudScope = "client") {
  if (isCompany(scope)) return api.delete<void>(`/company-gro-templates/risks/${riskId}`);
  return api.delete<void>(`/risks/${riskId}`);
}

export async function listRiskActionPlanModels(
  riskId: string,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) {
    const data = await getCompanyGroTemplates();
    return data.risk_action_plan_models
      .filter((item) => item.risk_catalog_id === riskId)
      .map((link) => {
        const plan = data.action_plan_models.find((item) => item.id === link.action_plan_model_id);
        return {
          id: link.id,
          action_plan_model_id: link.action_plan_model_id,
          risk_catalog_id: riskId,
          code: plan?.code ?? link.action_plan_code ?? "",
          title: plan?.title ?? link.action_plan_title,
          description: plan?.description ?? null,
          is_active: plan?.is_active ?? true,
          created_at: plan?.created_at ?? new Date(0).toISOString(),
          updated_at: plan?.updated_at ?? new Date(0).toISOString(),
          items_count: plan?.items_count ?? plan?.items?.length ?? 0,
        } satisfies RiskActionPlanModelLink;
      });
  }
  return api.get<RiskActionPlanModelLink[]>(`/risks/${riskId}/action-plan-models`);
}

export async function linkActionPlanModelToRisk(
  riskId: string,
  payload: RiskActionPlanModelLinkIn,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) {
    await createCompanyGroRiskActionLink(riskId, payload.action_plan_model_id);
    const linked = await listRiskActionPlanModels(riskId, scope);
    const created = linked.find((item) => item.action_plan_model_id === payload.action_plan_model_id);
    if (!created) throw new Error("Vínculo criado, mas não foi possível recarregá-lo.");
    return created;
  }
  return api.post<RiskActionPlanModelLink>(`/risks/${riskId}/action-plan-models`, payload);
}

export async function unlinkActionPlanModelFromRisk(
  riskId: string,
  actionPlanModelId: string,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) {
    const data = await getCompanyGroTemplates();
    const link = data.risk_action_plan_models.find(
      (item) => item.risk_catalog_id === riskId && item.action_plan_model_id === actionPlanModelId,
    );
    if (!link) throw new Error("Vínculo modelo não encontrado.");
    return deleteCompanyGroRiskActionLink(link.id);
  }
  return api.delete<void>(`/risks/${riskId}/action-plan-models/${actionPlanModelId}`);
}

export async function listAvailableActionPlanModels(
  params?: { q?: string; is_active?: boolean },
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) {
    const data = await getCompanyGroTemplates();
    const q = params?.q?.trim().toLocaleLowerCase("pt-BR") ?? "";
    return data.action_plan_models.filter((item) => {
      if (typeof params?.is_active === "boolean" && item.is_active !== params.is_active) return false;
      if (!q) return true;
      return [item.code, item.title, item.description]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase("pt-BR").includes(q));
    }) as ActionPlanModel[];
  }
  return api.get<ActionPlanModel[]>("/action-plan-models", {
    params: {
      q: params?.q || undefined,
      is_active: typeof params?.is_active === "boolean" ? params.is_active : undefined,
    },
  });
}
